import {
  type TicketListItem,
  type TicketListQuery,
  type TicketListSortBy,
} from '@gb-ticket/shared-types';
import { type ColumnDef, flexRender, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { ArrowDownIcon, ArrowUpIcon, ArrowUpDownIcon } from 'lucide-react';
import { useMemo } from 'react';
import { Link } from 'react-router';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  ChangeRiskBadge,
  MajorIncidentBadge,
  PriorityBadge,
  ProblemLifecycleBadge,
  StatusBadge,
} from '@/products/helpdesk/features/tickets/ticket-badges';
import { useAppPaths } from '@/products/helpdesk/features/portal/portal-paths';
import { columnAriaSort } from '@/lib/a11y';
import { formatDateTime } from '@/lib/format';
import { PersonChip } from '@/platform/components/person-chip';

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- interface params required by the library
  interface ColumnMeta<TData, TValue> {
    className?: string;
  }
}

type TicketsTableProps = {
  items: TicketListItem[];
  query: TicketListQuery;
  onSortChange: (sortBy: TicketListSortBy) => void;
};

const SORTABLE = new Set<string>([
  'number',
  'subject',
  'status',
  'priority',
  'createdAt',
  'updatedAt',
]);

export function TicketsTable({ items, query, onSortChange }: TicketsTableProps) {
  const paths = useAppPaths();
  const isPortal = paths.isPortal;
  const ticketPrefix = paths.tickets;
  const columns = useMemo<ColumnDef<TicketListItem>[]>(
    () => [
      {
        accessorKey: 'number',
        header: 'Nr.',
        cell: ({ row }) => <span className="font-medium">#{row.original.number}</span>,
      },
      {
        accessorKey: 'subject',
        header: 'Betreff',
        cell: ({ row }) => (
          <span className="inline-flex flex-wrap items-center gap-2">
            <Link
              to={`${ticketPrefix}/${row.original.number}`}
              className="font-medium text-foreground underline-offset-2 after:absolute after:inset-0 hover:underline focus-visible:underline"
              aria-label={
                isPortal
                  ? `Anfrage #${row.original.number} öffnen: ${row.original.subject}`
                  : `Ticket #${row.original.number} öffnen: ${row.original.subject}`
              }
            >
              {row.original.subject}
            </Link>
            {row.original.isMajor && <MajorIncidentBadge />}
            {row.original.problemLifecycle && (
              <ProblemLifecycleBadge lifecycle={row.original.problemLifecycle} />
            )}
            {row.original.changeRisk && <ChangeRiskBadge risk={row.original.changeRisk} />}
          </span>
        ),
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
      },
      {
        accessorKey: 'priority',
        header: 'Priorität',
        meta: { className: 'hidden sm:table-cell' },
        cell: ({ row }) => <PriorityBadge priority={row.original.priority} />,
      },
      ...(!isPortal
        ? ([
            {
              id: 'requester',
              header: 'Von',
              cell: ({ row }) => (
                <span
                  className="block max-w-52"
                  title={`${row.original.requester.displayName} · ${row.original.requester.email}`}
                >
                  <PersonChip
                    displayName={row.original.requester.displayName}
                    profilePhotoUrl={row.original.requester.profilePhotoUrl}
                  />
                </span>
              ),
            },
            {
              id: 'category',
              header: 'Kategorie',
              meta: { className: 'hidden lg:table-cell' },
              cell: ({ row }) =>
                row.original.category
                  ? `${row.original.category.parentName ? `${row.original.category.parentName} / ` : ''}${row.original.category.name}`
                  : '—',
            },
            {
              id: 'assignment',
              header: 'Zuweisung',
              meta: { className: 'hidden md:table-cell' },
              cell: ({ row }) =>
                row.original.assignee ? (
                  <PersonChip
                    displayName={row.original.assignee.displayName}
                    profilePhotoUrl={row.original.assignee.profilePhotoUrl}
                  />
                ) : (
                  (row.original.queue?.name ?? '—')
                ),
            },
          ] as ColumnDef<TicketListItem>[])
        : []),
      {
        accessorKey: 'createdAt',
        header: isPortal ? 'Erstellt am' : 'Erstellt',
        meta: { className: 'hidden xl:table-cell' },
        cell: ({ row }) => formatDateTime(row.original.createdAt),
      },
      {
        accessorKey: 'updatedAt',
        header: isPortal ? 'Zuletzt aktualisiert' : 'Aktualisiert',
        meta: { className: 'hidden lg:table-cell' },
        cell: ({ row }) => formatDateTime(row.original.updatedAt),
      },
    ],
    [isPortal, ticketPrefix],
  );

  const table = useReactTable({
    data: items,
    columns,
    getCoreRowModel: getCoreRowModel(),
    manualSorting: true,
    getRowId: (row) => row.id,
  });

  return (
    <Table>
      <TableHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <TableRow key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              const sortKey = header.column.id;
              const canSort = SORTABLE.has(sortKey);
              return (
                <TableHead
                  key={header.id}
                  className={header.column.columnDef.meta?.className}
                  aria-sort={
                    canSort ? columnAriaSort(sortKey, query.sortBy, query.sortOrder) : undefined
                  }
                >
                  {canSort ? (
                    <button
                      type="button"
                      className="relative z-10 inline-flex items-center gap-1 hover:text-foreground"
                      onClick={() => {
                        if (isSortBy(sortKey)) {
                          onSortChange(sortKey);
                        }
                      }}
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      <SortIcon active={query.sortBy === sortKey} order={query.sortOrder} />
                    </button>
                  ) : (
                    flexRender(header.column.columnDef.header, header.getContext())
                  )}
                </TableHead>
              );
            })}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {table.getRowModel().rows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
              Keine Tickets gefunden.
            </TableCell>
          </TableRow>
        ) : (
          table.getRowModel().rows.map((row) => (
            <TableRow key={row.id} className="relative cursor-pointer">
              {row.getVisibleCells().map((cell) => (
                <TableCell key={cell.id} className={cell.column.columnDef.meta?.className}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))
        )}
      </TableBody>
    </Table>
  );
}

function isSortBy(value: string): value is TicketListSortBy {
  return SORTABLE.has(value);
}

function SortIcon({ active, order }: { active: boolean; order: 'asc' | 'desc' }) {
  if (!active) {
    return <ArrowUpDownIcon className="size-3.5 text-muted-foreground" aria-hidden />;
  }
  return order === 'asc' ? (
    <ArrowUpIcon className="size-3.5" aria-hidden />
  ) : (
    <ArrowDownIcon className="size-3.5" aria-hidden />
  );
}
